/**
 * Phase 2 slice 2 — entity-owned investment property sale proceeds → entity liquid pool.
 * Ported from frontend/src/utils/entityInvestmentPropertySale.js. Originally a MINIMAL subset
 * (`creditLiquidProceeds`, `removeSoldInvestmentProperty`, `routesIpProceedsToEntityPool` — all
 * `calculations/retirementVillageCalculations.ts` needed); `getSelectedInvestmentProperty`,
 * `sumInvestmentPropertyAllocations`, `creditInvestmentPropertyUnallocatedProceeds`, and
 * `applyInvestmentPropertySaleToFactFind` were added when `suggestionEngine/supportAtHome.ts`
 * (the server-side port of strategy/SupportAtHomeStrategyApplicator.js's
 * `applySellEntityInvestmentPropertyStrategy` → utils/supportAtHomeSuggestionEngineFunding.js's
 * `applySahEntityIpSaleToFactFind`) needed the whole-property-sale flow too.
 */
import { entityOwnershipValue, getEntityIdFromOwnership, isEntityOwnership } from '../constants/clientStructureTypes.ts';
import { assetIdsMatch } from './entityFundingUtils.ts';
import { getInvestmentPropertyTaxSummary } from '../entityDisposalTax.ts';
import { applyDownsizerContributionsToAssets } from '../downsizerContributionUtils.ts';
import type { FactFind, FactFindAsset, FactFindAssetMap } from '../factFindTypes.ts';

export const ENTITY_IP_PROCEEDS_ID_PREFIX = 'entity-ip-proceeds-';

/**
 * Trust/company (and other non-partnership entities): cash stays in entity pool.
 * Partnership: gain taxed personally — unallocated proceeds use personal bank (spec §5.9.2).
 */
export function routesIpProceedsToEntityPool(factFind: FactFind, asset: FactFindAsset | null | undefined): boolean {
    if (!asset || !isEntityOwnership(asset.ownership)) return false;
    const entityId = getEntityIdFromOwnership(asset.ownership);
    const structure = (factFind.structures || []).find((s) => s.id === entityId);
    return structure?.type !== 'partnership';
}

function defaultBankIncomeRate(assets: FactFindAssetMap | undefined): number {
    const list = assets?.bankAccounts;
    if (list && list.length > 0) return list[0]?.incomeRate || 2;
    return 2;
}

/** Credit cash to bankAccounts — merge into existing entity bank when mergeEntityId set. */
export function creditLiquidProceeds(
    assets: FactFindAssetMap,
    amount: number,
    options: { ownership: string; id: string; description: string; mergeEntityId?: string; incomeRate?: number },
): { credited: number; bankId: string | null } {
    const proceeds = Math.max(0, amount || 0);
    if (proceeds <= 0) return { credited: 0, bankId: null };

    const { ownership, id, description, mergeEntityId, incomeRate } = options;
    if (!assets.bankAccounts) assets.bankAccounts = [];

    if (mergeEntityId) {
        const entityOwn = entityOwnershipValue(mergeEntityId);
        const existing = assets.bankAccounts.find(
            (a) => a.ownership === entityOwn && !String(a.id || '').startsWith(ENTITY_IP_PROCEEDS_ID_PREFIX),
        );
        if (existing) {
            existing.value = (existing.value || 0) + proceeds;
            return { credited: proceeds, bankId: existing.id != null ? String(existing.id) : null };
        }
    }

    const existingById = assets.bankAccounts.find((a) => assetIdsMatch(a.id, id));
    if (existingById) {
        existingById.value = (existingById.value || 0) + proceeds;
        return { credited: proceeds, bankId: existingById.id != null ? String(existingById.id) : null };
    }

    assets.bankAccounts.push({
        id,
        type: 'bankAccounts',
        description,
        value: proceeds,
        ownership,
        incomeRate: incomeRate ?? defaultBankIncomeRate(assets),
        growthRate: 0,
    });
    return { credited: proceeds, bankId: id };
}

export function removeSoldInvestmentProperty(assets: FactFindAssetMap | undefined, propertyId: string | number): void {
    if (!assets?.investmentProperty) return;
    assets.investmentProperty = assets.investmentProperty.filter((p) => !assetIdsMatch(p.id, propertyId));
}

export function getSelectedInvestmentProperty(factFind: FactFind, details: any): any {
    if (!factFind?.assets?.investmentProperty?.length || !details) return null;
    const selectedPropertyId = details.selectedPropertyId;
    return factFind.assets.investmentProperty.find((p) => assetIdsMatch(p.id, selectedPropertyId)) || factFind.assets.investmentProperty[0];
}

export function sumInvestmentPropertyAllocations(details: any = {}): number {
    return (
        (details.radAllocation || 0) +
        (details.newHomePurchasePrice || 0) +
        (details.downsizerContribution || 0) +
        (details.partnerDownsizerContribution || 0) +
        (details.giftingAmount || 0) +
        (details.funeralBondPurchase || 0)
    );
}

/** Credit unallocated net proceeds after allocations (preview or post-sale). */
export function creditInvestmentPropertyUnallocatedProceeds(data: any, factFind: FactFind, details: any): { unallocated: number; routedToEntity: boolean; entityId: string | null } {
    const property = getSelectedInvestmentProperty(factFind, details);
    const summary = getInvestmentPropertyTaxSummary(factFind, details);
    const netProceeds = summary?.netProceeds ?? (details.salePrice || 0) - (details.sellingCosts || 0);
    const unallocated = netProceeds - sumInvestmentPropertyAllocations(details);
    if (unallocated <= 0) {
        return { unallocated: 0, routedToEntity: false, entityId: null };
    }

    const incomeRate = defaultBankIncomeRate(data.assets);
    const toEntity = property && routesIpProceedsToEntityPool(factFind, property);

    if (toEntity) {
        const entityId = getEntityIdFromOwnership(property.ownership);
        const structure = (factFind.structures || []).find((s) => s.id === entityId);
        const name = structure?.name?.trim() || 'Trust or company';
        creditLiquidProceeds(data.assets, unallocated, {
            ownership: entityOwnershipValue(entityId as string),
            id: `${ENTITY_IP_PROCEEDS_ID_PREFIX}${entityId}`,
            description: `Unallocated proceeds — ${name} (from property sale)`,
            mergeEntityId: entityId as string,
            incomeRate,
        });
        return { unallocated, routedToEntity: true, entityId: entityId as string };
    }

    creditLiquidProceeds(data.assets, unallocated, {
        ownership: 'joint',
        id: 'investment-proceeds',
        description: 'Unallocated Investment Sale Proceeds',
        incomeRate,
    });
    return { unallocated, routedToEntity: false, entityId: null };
}

/**
 * Apply whole investment property sale to a cloned factFind (Aged Care manual control /
 * Support at Home suggestion engine).
 * @param data — mutable clone
 * @param factFind — baseline for tax / structure lookup
 * @param details — investmentPropertyDetails
 */
export function applyInvestmentPropertySaleToFactFind(data: any, factFind: FactFind, details: any): any {
    const property = getSelectedInvestmentProperty(factFind, details);
    if (!property) return data;

    removeSoldInvestmentProperty(data.assets, property.id);

    if ((details.newHomePurchasePrice || 0) > 0) {
        data.home.value = details.newHomePurchasePrice;
    }

    if ((details.radAllocation || 0) > 0) {
        if (!data.assets.radPaid) data.assets.radPaid = [];
        data.assets.radPaid = [
            {
                id: 'rad-paid',
                type: 'radPaid',
                description: 'RAD Paid',
                value: details.radAllocation,
                ownership: 'client',
            },
        ];
    }

    creditInvestmentPropertyUnallocatedProceeds(data, factFind, details);

    applyDownsizerContributionsToAssets(data.assets, factFind, details, {
        idPrefix: 'investment-downsizer-contrib',
        assetId: 'investment-downsizer-contrib',
        partnerAssetId: 'partner-investment-downsizer-contrib',
    });

    if ((details.funeralBondPurchase || 0) > 0) {
        if (!data.assets.funeralBonds) data.assets.funeralBonds = [];
        data.assets.funeralBonds.push({
            id: 'investment-fbond',
            type: 'funeralBonds',
            description: 'Funeral Bond from Proceeds',
            value: details.funeralBondPurchase,
            ownership: 'client',
        });
    }

    return data;
}
